/**
 * Quiz Coffee Match
 * State machine, scoring engine, and UI controller for the coffee quiz section.
 * No dependencies beyond jQuery (already loaded by theme).
 */
(function() {
  'use strict';

  var SECTION_SELECTOR = '.quiz-coffee-match';
  var TRANSITION_MS = 200;
  var MOBILE_VIDEO_BREAKPOINT = 798;
  var WELCOME_VIDEO_ROOT_MARGIN = '1400px 0px';

  // Range maps for scoring
  var RANGE_MAP = {
    'low':    [1, 2],
    'medium': [3, 4],
    'high':   [5, 6],
    'any':    null
  };

  // Size label to variant matching keywords
  var SIZE_KEYWORDS = {
    '12oz': ['12oz', '12 oz'],
    '1lb':  ['1lb', '1 lb', '16oz', '16 oz'],
    '2lb':  ['2lb', '2 lb', '32oz', '32 oz']
  };

  // Adventure level labels
  var ADVENTURE_LABELS = ['Comfort Zone', 'Curious Explorer', 'Flavor Chaser'];

  // Find first product with 'decaf' in its handle (collection order)
  function findDecafProduct(products) {
    for (var i = 0; i < products.length; i++) {
      if (products[i].handle && products[i].handle.indexOf('decaf') > -1) {
        return products[i];
      }
    }
    return null;
  }

  function QuizCoffeeMatch(section) {
    this.$section = $(section);
    this.sectionId = this.$section.data('section-id');
    this.totalScreens = parseInt(this.$section.data('total-screens'), 10);
    this.animationsEnabled = this.$section.data('enable-animations') !== false;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.currentScreen = 0;
    this.answers = [];        // Array of arrays: each question index holds selected answer elements
    this.adventureLevel = null;
    this.preferredSize = null;
    this.products = [];
    this.selectedVariantId = null;
    this.transitioning = false;
    this.screenArrivedAt = 0;     // Timestamp of last screen transition (guards stale clicks)
    this.autoAdvanceTimer = null;
    this.addToCartLabel = '';
    this.$welcomeVideos = this.$section.find('.qcm-welcome-video__media');
    this.welcomeVideoObserver = null;
    this.welcomeVideoInView = false;
    this.welcomeVideoResizeHandler = null;
    this.welcomeVideoResizeTimer = null;
    this.welcomeVideoInteractionHandler = null;

    this.init();
  }

  QuizCoffeeMatch.prototype.init = function() {
    this.loadProducts();
    this.setupWelcomeVideo();
    this.bindEvents();
    // Welcome screen is already visible via is-active class in HTML.
    // No showScreen(0) call needed; avoids unnecessary transition lock.
  };

  QuizCoffeeMatch.prototype.loadProducts = function() {
    var $json = $('script[data-quiz-products="' + this.sectionId + '"]');
    if ($json.length) {
      try {
        this.products = JSON.parse($json.html()) || [];
      } catch (e) {
        this.products = [];
      }
    }
  };

  QuizCoffeeMatch.prototype.bindEvents = function() {
    var self = this;

    // Start button
    this.$section.on('click', '[data-action="start"]', function() {
      self.pauseWelcomeVideos();
      self.goToScreen(1);
    });

    // Answer selection (skip hidden slider answers — those are handled by slider interaction)
    this.$section.on('click', '.qcm-answer', function() {
      if ($(this).hasClass('qcm-answer--slider')) return;
      self.handleAnswerClick($(this));
    });

    // Next button
    this.$section.on('click', '[data-action="next"]', function() {
      if (!$(this).prop('disabled')) {
        self.goToScreen(self.currentScreen + 1);
      }
    });

    // Back button
    this.$section.on('click', '[data-action="back"]', function() {
      self.goToScreen(self.currentScreen - 1, true);
    });

    // Add to cart
    this.$section.on('click', '[data-action="add-to-cart"]', function() {
      self.addToCart($(this));
    });

    // Variant selection
    this.$section.on('click', '.qcm-variant-btn', function() {
      self.selectVariant($(this));
    });

    // Retake
    this.$section.on('click', '[data-action="retake"]', function() {
      self.retake();
    });

    // Keyboard support for answers
    this.$section.on('keydown', '.qcm-answer', function(e) {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        $(this).trigger('click');
      }
    });

    // Slider stop and label clicks
    this.$section.on('click', '.qcm-slider__stop', function() {
      self.handleSliderClick($(this));
    });
    this.$section.on('click', '.qcm-slider__label', function() {
      var labelIndex = $(this).index();
      var $stop = $(this).closest('.qcm-slider').find('.qcm-slider__stop').eq(labelIndex);
      if ($stop.length) {
        self.handleSliderClick($stop);
      }
    });

    // Slider thumb keyboard support (left/right arrows)
    this.$section.on('keydown', '.qcm-slider__thumb', function(e) {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      e.preventDefault();
      var $slider = $(this).closest('.qcm-slider');
      var $stops = $slider.find('.qcm-slider__stop');
      var $active = $stops.filter('.is-active');
      var currentIndex = $active.length ? parseInt($active.data('stop'), 10) : -1;
      var nextIndex;

      if (e.key === 'ArrowRight') {
        nextIndex = Math.min(currentIndex + 1, $stops.length - 1);
      } else {
        nextIndex = Math.max(currentIndex - 1, 0);
      }

      if (nextIndex !== currentIndex) {
        self.handleSliderClick($stops.eq(nextIndex));
      }
    });

    // Slider drag support (mouse + touch)
    this.$section.on('mousedown touchstart', '.qcm-slider__thumb, .qcm-slider__track', function(e) {
      var $slider = $(this).closest('.qcm-slider');
      var $track = $slider.find('.qcm-slider__track');
      var $thumb = $slider.find('.qcm-slider__thumb');
      var $stops = $slider.find('.qcm-slider__stop');
      var totalStops = $stops.length;
      if (totalStops < 2) return;

      e.preventDefault();

      // Disable transition during drag for immediate feedback
      $thumb.addClass('is-dragging');
      $slider.find('.qcm-slider__fill').addClass('is-dragging');

      function getX(ev) {
        var orig = ev.originalEvent || ev;
        if (orig.touches && orig.touches.length) return orig.touches[0].clientX;
        return orig.clientX;
      }

      function pctFromEvent(ev) {
        var rect = $track[0].getBoundingClientRect();
        var x = getX(ev) - rect.left;
        return Math.max(0, Math.min(1, x / rect.width));
      }

      function updateThumbLive(pct) {
        $thumb.css('left', (pct * 100) + '%');
        $slider.find('.qcm-slider__fill').css('width', (pct * 100) + '%');
      }

      function nearestStop(pct) {
        var idx = Math.round(pct * (totalStops - 1));
        return Math.max(0, Math.min(totalStops - 1, idx));
      }

      // Track last known position (touchend has no coordinates)
      var lastPct = pctFromEvent(e);

      // Immediately update on initial press position
      updateThumbLive(lastPct);

      function onMove(ev) {
        lastPct = pctFromEvent(ev);
        updateThumbLive(lastPct);
      }

      function onEnd() {
        $(document).off('mousemove touchmove', onMove);
        $(document).off('mouseup touchend touchcancel', onEnd);

        // Re-enable transitions
        $thumb.removeClass('is-dragging');
        $slider.find('.qcm-slider__fill').removeClass('is-dragging');

        // Snap to nearest stop using last known position
        var stopIdx = nearestStop(lastPct);
        self.handleSliderClick($stops.eq(stopIdx));
      }

      $(document).on('mousemove touchmove', onMove);
      $(document).on('mouseup touchend touchcancel', onEnd);
    });
  };

  QuizCoffeeMatch.prototype.handleAnswerClick = function($answer) {
    // Ignore clicks that arrive within 300ms of a screen transition
    // (prevents ghost taps from previous screen registering as selections)
    if (Date.now() - this.screenArrivedAt < 300) return;

    var $screen = $answer.closest('.qcm-screen');
    var selectType = $screen.data('select-type');
    var questionIndex = parseInt($screen.data('question-index'), 10);

    if (selectType === 'single') {
      // Deselect siblings
      $screen.find('.qcm-answer').attr('aria-checked', 'false');
      $answer.attr('aria-checked', 'true');
    } else {
      // Toggle multi
      var pressed = $answer.attr('aria-checked') === 'true';
      $answer.attr('aria-checked', pressed ? 'false' : 'true');
    }

    // Store selection
    if (!this.answers[questionIndex]) {
      this.answers[questionIndex] = [];
    }
    var selected = $screen.find('.qcm-answer[aria-checked="true"]');
    this.answers[questionIndex] = selected.toArray();

    // Track adventure level and size from answer labels
    var label = $answer.find('.qcm-answer__label').text().trim();
    if (ADVENTURE_LABELS.indexOf(label) > -1) {
      this.adventureLevel = label;
    }
    if (SIZE_KEYWORDS[label]) {
      this.preferredSize = label;
    }

    // Enable/disable next button
    var $next = $screen.find('[data-action="next"]');
    $next.prop('disabled', selected.length === 0);

    // Update gauge indicator if gauge style
    this.updateGaugeIndicator($screen);

    // Populate gauge description reveal
    var $gaugeDescReveal = $screen.find('.qcm-gauge__desc-reveal');
    if ($gaugeDescReveal.length) {
      var desc = $answer.find('.qcm-answer__desc').text();
      $gaugeDescReveal.text(desc || '');
    }

    // No auto-advance — user must click Next to proceed
  };

  // ---- Gauge indicator ----

  QuizCoffeeMatch.prototype.updateGaugeIndicator = function($screen) {
    var $answers = $screen.find('.qcm-answers--gauge');
    if (!$answers.length) return;

    var $indicator = $answers.find('.qcm-gauge__indicator');
    var $selected = $answers.find('.qcm-answer[aria-checked="true"]');

    if (!$selected.length) {
      $indicator.css('opacity', '0');
      return;
    }

    // Use offsetLeft/offsetWidth for position relative to the
    // container's padding box (matches absolute positioning context)
    var btn = $selected[0];

    $indicator.css({
      'opacity': '1',
      'left': btn.offsetLeft + 'px',
      'width': btn.offsetWidth + 'px',
      'transform': 'none'
    });
  };

  // ---- Slider interaction ----

  QuizCoffeeMatch.prototype.handleSliderClick = function($stop) {
    if (Date.now() - this.screenArrivedAt < 300) return;

    var $screen = $stop.closest('.qcm-screen');
    var stopIndex = parseInt($stop.data('stop'), 10);
    var $slider = $stop.closest('.qcm-slider');
    var $answers = $screen.find('.qcm-answer');
    var totalStops = $slider.find('.qcm-slider__stop').length;

    // Select the corresponding hidden answer button
    $answers.attr('aria-checked', 'false');
    $answers.eq(stopIndex).attr('aria-checked', 'true');

    // Update thumb position
    var pct = totalStops > 1 ? (stopIndex / (totalStops - 1)) * 100 : 50;
    $slider.find('.qcm-slider__thumb').css('left', pct + '%').attr({
      'aria-valuenow': stopIndex,
      'aria-valuetext': $answers.eq(stopIndex).find('.qcm-answer__label').text()
    });

    // Update fill
    $slider.find('.qcm-slider__fill').css('width', pct + '%');

    // Mark active stop
    $slider.find('.qcm-slider__stop').removeClass('is-active');
    $stop.addClass('is-active');

    // Mark active label
    var $labels = $slider.find('.qcm-slider__label');
    $labels.removeClass('is-active');
    $labels.eq(stopIndex).addClass('is-active');

    // Show description
    var $descReveal = $screen.find('.qcm-slider__desc-reveal');
    if ($descReveal.length) {
      var desc = $answers.eq(stopIndex).find('.qcm-answer__desc').text();
      $descReveal.text(desc || '');
    }

    // Track adventure level and size from answer labels
    var label = $answers.eq(stopIndex).find('.qcm-answer__label').text().trim();
    if (ADVENTURE_LABELS.indexOf(label) > -1) {
      this.adventureLevel = label;
    }
    if (SIZE_KEYWORDS[label]) {
      this.preferredSize = label;
    }

    // Store in answers array
    var questionIndex = parseInt($screen.data('question-index'), 10);
    this.answers[questionIndex] = [$answers[stopIndex]];

    // Enable next button (no auto-advance — user needs time to read descriptions)
    $screen.find('[data-action="next"]').prop('disabled', false);
  };

  // ---- Screen transitions ----

  QuizCoffeeMatch.prototype.goToScreen = function(index, isBack) {
    if (this.transitioning || index < 0 || index >= this.totalScreens) return;
    if (index === this.currentScreen) return;

    // If going to results, compute scores
    if (index === this.totalScreens - 1) {
      this.computeAndShowResults();
    }

    this.showScreen(index, isBack);
  };

  QuizCoffeeMatch.prototype.showScreen = function(index, isBack) {
    var self = this;
    var $screens = this.$section.find('.qcm-screen');
    var $current = $screens.filter('.is-active');
    var $next = $screens.filter('[data-screen="' + index + '"]');

    if (!$next.length) return;

    var shouldAnimate = this.animationsEnabled && !this.reducedMotion && $current.length;

    // Block pointer events on the incoming screen to prevent ghost taps
    // from the previous screen's touch registering as answer selections
    $next.css('pointer-events', 'none');

    if (shouldAnimate) {
      this.transitioning = true;

      // Exit current
      $current.removeClass('is-active').addClass(isBack ? 'is-exiting-back' : 'is-exiting');

      // Enter next
      $next.addClass(isBack ? 'is-entering-back' : 'is-entering');

      // Force reflow
      $next[0].offsetHeight;

      // Activate
      setTimeout(function() {
        $current.removeClass('is-exiting is-exiting-back');
        $next.removeClass('is-entering is-entering-back').addClass('is-active');
        self.transitioning = false;
        self.onScreenActive(index);
      }, TRANSITION_MS);
    } else {
      $current.removeClass('is-active is-exiting is-exiting-back is-entering is-entering-back');
      $next.addClass('is-active');
      this.onScreenActive(index);
    }

    this.currentScreen = index;
    this.updateProgress(index);

    // Toggle results-active class for background color swap
    var isResults = index === this.totalScreens - 1;
    this.$section.toggleClass('qcm--results-active', isResults);
  };

  QuizCoffeeMatch.prototype.onScreenActive = function(index) {
    this.screenArrivedAt = Date.now();

    var $screen = this.$section.find('.qcm-screen[data-screen="' + index + '"]');

    // Re-enable pointer events after ghost-tap window expires
    // (mobile touch→click delay is typically 300ms; 400ms gives margin)
    setTimeout(function() {
      $screen.css('pointer-events', '');
    }, 400);

    if (index === 0) {
      this.handleWelcomeVideoPlayback();
    } else {
      this.pauseWelcomeVideos();
    }

    // Focus management for accessibility
    var $focusTarget = $screen.find('h2, .global-button').first();
    if ($focusTarget.length) {
      $focusTarget.attr('tabindex', '-1').focus();
    }
  };

  // ---- Welcome video ----

  QuizCoffeeMatch.prototype.setupWelcomeVideo = function() {
    if (!this.$welcomeVideos.length) return;

    this.prepareWelcomeVideos();

    if (this.reducedMotion) {
      this.pauseWelcomeVideos();
      return;
    }

    this.observeWelcomeVideo();
    this.bindWelcomeVideoResize();
    this.queueWelcomeVideoVisibilityFallback();
  };

  QuizCoffeeMatch.prototype.prepareWelcomeVideos = function() {
    this.$welcomeVideos.each(function() {
      this.defaultMuted = true;
      this.muted = true;
      this.playsInline = true;
      this.removeAttribute('controls');
      this.setAttribute('muted', '');
      this.setAttribute('playsinline', '');
      this.setAttribute('webkit-playsinline', '');
    });
  };

  QuizCoffeeMatch.prototype.observeWelcomeVideo = function() {
    var self = this;

    if ('IntersectionObserver' in window) {
      this.welcomeVideoObserver = new IntersectionObserver(function(entries) {
        entries.forEach(function(entry) {
          self.welcomeVideoInView = entry.isIntersecting;
          self.handleWelcomeVideoPlayback();
        });
      }, { rootMargin: WELCOME_VIDEO_ROOT_MARGIN, threshold: 0 });

      this.welcomeVideoObserver.observe(this.$section[0]);
    } else {
      this.welcomeVideoInView = true;
      this.handleWelcomeVideoPlayback();
    }
  };

  QuizCoffeeMatch.prototype.queueWelcomeVideoVisibilityFallback = function() {
    var self = this;

    setTimeout(function() {
      if (self.currentScreen !== 0 || self.welcomeVideoInView) return;

      var rect = self.$section[0].getBoundingClientRect();
      self.welcomeVideoInView = rect.bottom > 0 && rect.top < window.innerHeight;
      self.handleWelcomeVideoPlayback();
    }, 250);
  };

  QuizCoffeeMatch.prototype.bindWelcomeVideoResize = function() {
    var self = this;

    this.welcomeVideoResizeHandler = function() {
      clearTimeout(self.welcomeVideoResizeTimer);
      self.welcomeVideoResizeTimer = setTimeout(function() {
        self.handleWelcomeVideoPlayback();
      }, 150);
    };

    window.addEventListener('resize', this.welcomeVideoResizeHandler);
  };

  QuizCoffeeMatch.prototype.getActiveWelcomeVideo = function() {
    if (!this.$welcomeVideos.length) return null;
    if (this.$welcomeVideos.length === 1) return this.$welcomeVideos[0];

    var isMobile = window.innerWidth <= MOBILE_VIDEO_BREAKPOINT;
    var $preferred = isMobile
      ? this.$welcomeVideos.filter('.qcm-welcome-video__media--mobile')
      : this.$welcomeVideos.filter('.qcm-welcome-video__media--desktop');

    return $preferred[0] || this.$welcomeVideos[0];
  };

  QuizCoffeeMatch.prototype.handleWelcomeVideoPlayback = function() {
    if (!this.$welcomeVideos.length) return;

    if (this.reducedMotion || this.currentScreen !== 0 || !this.welcomeVideoInView) {
      this.pauseWelcomeVideos();
      return;
    }

    var activeVideo = this.getActiveWelcomeVideo();
    if (!activeVideo) return;

    this.$welcomeVideos.off('loadeddata.qcmWelcomeVideo');
    this.pauseInactiveWelcomeVideos(activeVideo);
    this.prepareActiveWelcomeVideo(activeVideo);
    this.playWelcomeVideo(activeVideo);
  };

  QuizCoffeeMatch.prototype.pauseInactiveWelcomeVideos = function(activeVideo) {
    this.$welcomeVideos.each(function() {
      if (this !== activeVideo) {
        this.pause();
        this.removeAttribute('autoplay');
      }
    });
  };

  QuizCoffeeMatch.prototype.prepareActiveWelcomeVideo = function(video) {
    video.defaultMuted = true;
    video.muted = true;
    video.playsInline = true;
    video.preload = 'auto';
    video.removeAttribute('controls');
    video.setAttribute('muted', '');
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');
    video.setAttribute('preload', 'auto');
  };

  QuizCoffeeMatch.prototype.playWelcomeVideo = function(video) {
    var self = this;

    video.muted = true;
    video.setAttribute('autoplay', '');

    var playPromise = video.play();

    if (playPromise !== undefined) {
      playPromise.catch(function(error) {
        if (error.name === 'NotAllowedError') {
          self.handleWelcomeAutoplayBlocked(video);
        }
      });
    }
  };

  QuizCoffeeMatch.prototype.handleWelcomeAutoplayBlocked = function(video) {
    var self = this;

    this.clearWelcomeVideoInteraction();
    this.welcomeVideoInteractionHandler = function() {
      video.muted = true;
      video.play().catch(function() {});
      self.clearWelcomeVideoInteraction();
    };

    document.addEventListener('click', this.welcomeVideoInteractionHandler, { once: true });
    document.addEventListener('touchstart', this.welcomeVideoInteractionHandler, { once: true });
  };

  QuizCoffeeMatch.prototype.clearWelcomeVideoInteraction = function() {
    if (!this.welcomeVideoInteractionHandler) return;

    document.removeEventListener('click', this.welcomeVideoInteractionHandler);
    document.removeEventListener('touchstart', this.welcomeVideoInteractionHandler);
    this.welcomeVideoInteractionHandler = null;
  };

  QuizCoffeeMatch.prototype.pauseWelcomeVideos = function() {
    if (!this.$welcomeVideos.length) return;

    this.clearWelcomeVideoInteraction();
    this.$welcomeVideos.off('loadeddata.qcmWelcomeVideo');
    this.$welcomeVideos.each(function() {
      this.pause();
      this.removeAttribute('autoplay');
    });
  };

  QuizCoffeeMatch.prototype.updateProgress = function(index) {
    var $progress = this.$section.find('.qcm-progress');
    var isQuestion = index > 0 && index < this.totalScreens - 1;

    if (isQuestion) {
      $progress.show();
      var questionIndex = index - 1;
      var totalQuestions = this.totalScreens - 2;
      var pct = ((questionIndex + 1) / totalQuestions) * 100;
      $progress.find('.qcm-progress__fill').css('width', pct + '%');

      $progress.find('.qcm-progress__dot').each(function(i) {
        $(this).toggleClass('is-complete', i < questionIndex)
               .toggleClass('is-active', i === questionIndex);
      });
    } else {
      $progress.hide();
    }
  };

  // ---- Scoring engine ----

  QuizCoffeeMatch.prototype.computeAndShowResults = function() {
    if (this.products.length === 0) {
      this.showFallback();
      return;
    }

    var preferences = this.collectPreferences();
    var scored = this.scoreProducts(preferences);

    // Sort by score desc (ties broken by original array order = collection position)
    scored.sort(function(a, b) { return b.score - a.score; });

    // Decaf handling: always show as runner, never as primary match
    var decafProduct = findDecafProduct(this.products);
    var scoredFiltered = scored;
    if (decafProduct) {
      scoredFiltered = scored.filter(function(s) {
        return s.product !== decafProduct;
      });
    }

    var topMatch = scoredFiltered[0];
    var runnersUp;

    if (decafProduct) {
      var decafEntry = { product: decafProduct, score: 0 };
      if (this.adventureLevel === 'Comfort Zone') {
        // Comfort Zone: decaf only
        runnersUp = [decafEntry];
      } else {
        // Explorer / Chaser: 1 scored runner + decaf as 2nd
        var scoredRunner = scoredFiltered.length > 1 ? scoredFiltered[1] : null;
        runnersUp = scoredRunner ? [scoredRunner, decafEntry] : [decafEntry];
      }
    } else {
      // No decaf in collection — fall back to current behavior
      var topCount = 3;
      if (this.adventureLevel === 'Comfort Zone') {
        topCount = 1;
      }
      runnersUp = scoredFiltered.slice(1, topCount);
    }

    this.renderMatch(topMatch.product);
    this.renderRunnersUp(runnersUp);
    this.triggerConfetti();
  };

  QuizCoffeeMatch.prototype.collectPreferences = function() {
    var prefs = { acidity: [], sweetness: [], body: [] };

    for (var q = 0; q < this.answers.length; q++) {
      var selected = this.answers[q];
      if (!selected) continue;

      for (var a = 0; a < selected.length; a++) {
        var $el = $(selected[a]);
        var dims = ['acidity', 'sweetness', 'body'];
        for (var d = 0; d < dims.length; d++) {
          var val = $el.data(dims[d]);
          if (val && val !== 'any') {
            var range = RANGE_MAP[val];
            if (range) {
              prefs[dims[d]].push(range);
            }
          }
        }
      }
    }
    return prefs;
  };

  QuizCoffeeMatch.prototype.scoreProducts = function(prefs) {
    var isFlavorChaser = this.adventureLevel === 'Flavor Chaser';

    return this.products.map(function(product) {
      var score = 0;
      var dims = ['acidity', 'sweetness', 'body'];
      var hasExtreme = false;

      for (var d = 0; d < dims.length; d++) {
        var dim = dims[d];
        var val = product[dim];
        var ranges = prefs[dim];

        for (var r = 0; r < ranges.length; r++) {
          if (val >= ranges[r][0] && val <= ranges[r][1]) {
            score += 1;
          }
        }

        // Track if any dimension hits 6
        if (val === 6) {
          hasExtreme = true;
        }
      }

      // Flavor Chaser bonus: +1 per product if any intensity is at 6
      if (isFlavorChaser && hasExtreme) {
        score += 1;
      }

      return { product: product, score: score };
    });
  };

  // ---- Results rendering ----

  QuizCoffeeMatch.prototype.renderMatch = function(product) {
    var $match = this.$section.find('.qcm-match');
    var $fallback = this.$section.find('.qcm-fallback');

    $fallback.hide();
    $match.show();

    // Image
    var $img = $match.find('.qcm-match__image');
    $img.html('<img src="' + this.escapeHtml(product.image) + '" srcset="' + this.escapeHtml(product.image_srcset) + '" sizes="(max-width: 599px) 160px, 200px" alt="' + this.escapeHtml(product.title) + '" width="200" loading="lazy">');

    // Title (linked to product)
    $match.find('.qcm-match__title').html('<a href="' + this.escapeHtml(product.url) + '" style="color: inherit; text-decoration: none;">' + this.escapeHtml(product.title) + '</a>');

    // Loox rating
    var $rating = $match.find('.qcm-match__rating');
    $rating.empty();
    if (product.loox_avg_rating !== null && product.loox_num_reviews !== null) {
      $rating.html('<div class="loox-rating" data-id="' + product.id +
        '" data-rating="' + product.loox_avg_rating +
        '" data-raters="' + product.loox_num_reviews + '"></div>');
    }

    // Price
    this.renderPrice($match, product.price, product.price_formatted, product.compare_at_price, product.compare_at_price_formatted);

    // Variants
    this.renderVariants(product, $match);

    // Enable add to cart
    $match.find('[data-action="add-to-cart"]').prop('disabled', false);

    // Trigger Loox to discover new rating divs
    this.triggerLooxRescan();
  };

  var FREE_SHIPPING_THRESHOLD = 4000; // $40.00 in cents
  var SHIPPING_ICON_SVG =
    '<svg class="qcm-match__shipping-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#333333" stroke-miterlimit="10" stroke-width="1.91" aria-hidden="true">' +
      '<circle cx="6.27" cy="17.73" r="1.91"/>' +
      '<circle cx="17.73" cy="17.73" r="1.91"/>' +
      '<polyline points="4.36 17.73 1.5 17.73 1.5 4.36 18.68 4.36 18.68 8.18 20.59 12 22.5 13.07 22.5 17.73 19.64 17.73"/>' +
      '<line x1="15.82" y1="17.73" x2="8.18" y2="17.73"/>' +
      '<polyline points="20.59 12 14.86 12 14.86 8.18 18.68 8.18"/>' +
    '</svg>';
  var FREE_SHIPPING_HTML = SHIPPING_ICON_SVG +
    '<span class="qcm-match__shipping-text">Ships free.</span>';
  var UNDER_THRESHOLD_HTML = SHIPPING_ICON_SVG +
    '<span class="qcm-match__shipping-text">Free shipping over $40</span>';

  QuizCoffeeMatch.prototype.renderPrice = function($container, price, priceFormatted, compareAtPrice, compareAtPriceFormatted) {
    var $el = $container.find('.qcm-match__price, .qcm-runner-card__price');
    if (compareAtPrice && compareAtPrice > price) {
      $el.html(
        '<span class="price sale">' +
          '<span class="current_price"><span class="money">' + this.escapeHtml(priceFormatted) + '</span></span> ' +
          '<span class="was_price"><span class="money">' + this.escapeHtml(compareAtPriceFormatted) + '</span></span>' +
        '</span>'
      );
    } else {
      $el.html('<span class="money">' + this.escapeHtml(priceFormatted) + '</span>');
    }

    // Free shipping indicator (primary match only)
    var $shipping = $container.find('.qcm-match__shipping');
    if ($shipping.length) {
      if (price >= FREE_SHIPPING_THRESHOLD) {
        $shipping.html(FREE_SHIPPING_HTML).show();
      } else {
        $shipping.html(UNDER_THRESHOLD_HTML).show();
      }
    }
  };

  QuizCoffeeMatch.prototype.triggerLooxRescan = function() {
    setTimeout(function() {
      if (window.loox && typeof window.loox.rerender === 'function') {
        window.loox.rerender();
      }
      if (typeof CustomEvent !== 'undefined') {
        document.dispatchEvent(new CustomEvent('loox:domchanged'));
      }
    }, 100);
  };

  QuizCoffeeMatch.prototype.renderVariants = function(product, $match) {
    var $container = $match.find('.qcm-match__variants');
    $container.empty();

    if (product.variants.length <= 1) {
      // Single variant, no selector needed
      this.selectedVariantId = product.variants[0].id;
      return;
    }

    var preferredSize = this.preferredSize;
    var firstAvailable = null;
    var matched = null;

    for (var i = 0; i < product.variants.length; i++) {
      var v = product.variants[i];
      var $btn = $('<button type="button" class="qcm-variant-btn"></button>')
        .text(v.title)
        .data('variant-id', v.id)
        .data('price', v.price_formatted)
        .data('price-raw', v.price)
        .data('compare-at-price', v.compare_at_price)
        .data('compare-at-price-formatted', v.compare_at_price_formatted);

      if (!v.available) {
        $btn.prop('disabled', true).css('opacity', '0.4');
      } else {
        if (!firstAvailable) firstAvailable = v;
        // Try to match preferred size
        if (preferredSize && SIZE_KEYWORDS[preferredSize]) {
          var keywords = SIZE_KEYWORDS[preferredSize];
          var option = (v.option1 || '').toLowerCase();
          for (var k = 0; k < keywords.length; k++) {
            if (option.indexOf(keywords[k]) > -1) {
              matched = v;
            }
          }
        }
      }

      $container.append($btn);
    }

    // Select best variant
    var selected = matched || firstAvailable || product.variants[0];
    this.selectedVariantId = selected.id;
    $container.find('.qcm-variant-btn').each(function() {
      if ($(this).data('variant-id') === selected.id) {
        $(this).addClass('is-selected');
      }
    });

    // Update price to selected variant
    this.renderPrice($match, selected.price, selected.price_formatted, selected.compare_at_price, selected.compare_at_price_formatted);
  };

  QuizCoffeeMatch.prototype.selectVariant = function($btn) {
    if ($btn.prop('disabled')) return;

    $btn.closest('.qcm-match__variants').find('.qcm-variant-btn').removeClass('is-selected');
    $btn.addClass('is-selected');
    this.selectedVariantId = $btn.data('variant-id');

    // Update displayed price with strikethrough support
    this.renderPrice(
      this.$section.find('.qcm-match'),
      $btn.data('price-raw'),
      $btn.data('price'),
      $btn.data('compare-at-price'),
      $btn.data('compare-at-price-formatted')
    );
  };

  QuizCoffeeMatch.prototype.renderRunnersUp = function(runnersUp) {
    var $runners = this.$section.find('.qcm-runners');
    if (!$runners.length || runnersUp.length === 0) {
      $runners.hide();
      return;
    }

    var $grid = $runners.find('.qcm-runners__grid');
    $grid.empty();

    for (var i = 0; i < runnersUp.length; i++) {
      var p = runnersUp[i].product;

      // Build Loox rating HTML
      var ratingHtml = '';
      if (p.loox_avg_rating !== null && p.loox_num_reviews !== null) {
        ratingHtml = '<div class="loox-rating" data-id="' + p.id +
          '" data-rating="' + p.loox_avg_rating +
          '" data-raters="' + p.loox_num_reviews + '"></div>';
      }

      // Build price HTML with strikethrough
      var priceHtml;
      if (p.compare_at_price && p.compare_at_price > p.price) {
        priceHtml = '<span class="qcm-runner-card__price">' +
          '<span class="price sale">' +
            '<span class="current_price"><span class="money">' + this.escapeHtml(p.price_formatted) + '</span></span> ' +
            '<span class="was_price"><span class="money">' + this.escapeHtml(p.compare_at_price_formatted) + '</span></span>' +
          '</span></span>';
      } else {
        priceHtml = '<span class="qcm-runner-card__price"><span class="money">' + this.escapeHtml(p.price_formatted) + '</span></span>';
      }

      // Card: Image > Title > Rating > Price
      var card = '<a class="qcm-runner-card" href="' + this.escapeHtml(p.url) + '">' +
        '<img src="' + this.escapeHtml(p.image) + '" alt="' + this.escapeHtml(p.title) + '" width="120" loading="lazy">' +
        '<span class="qcm-runner-card__title">' + this.escapeHtml(p.title) + '</span>' +
        ratingHtml +
        priceHtml +
        '</a>';
      $grid.append(card);
    }

    $runners.show();
    this.triggerLooxRescan();
  };

  QuizCoffeeMatch.prototype.showFallback = function() {
    this.$section.find('.qcm-match').hide();
    this.$section.find('.qcm-runners').hide();
    this.$section.find('.qcm-upsell').hide();
    this.$section.find('.qcm-fallback').show();
  };

  // ---- Confetti ----

  QuizCoffeeMatch.prototype.triggerConfetti = function() {
    if (this.reducedMotion) return;

    var $confetti = this.$section.find('.qcm-confetti');
    if (!$confetti.length) return;

    $confetti.empty();
    var colors = ['#A65971', '#3D4C51', '#F9F6F3', '#934A6D', '#DCDCDC'];
    var pieces = 40;

    for (var i = 0; i < pieces; i++) {
      var $piece = $('<div class="qcm-confetti__piece"></div>');
      $piece.css({
        left: Math.random() * 100 + '%',
        top: -10 + 'px',
        backgroundColor: colors[Math.floor(Math.random() * colors.length)],
        animationDelay: (Math.random() * 0.8) + 's',
        animationDuration: (1.5 + Math.random()) + 's',
        width: (5 + Math.random() * 6) + 'px',
        height: (5 + Math.random() * 6) + 'px',
        borderRadius: Math.random() > 0.5 ? '50%' : '1px',
        transform: 'rotate(' + Math.floor(Math.random() * 360) + 'deg)'
      });
      $confetti.append($piece);
    }

    // Cleanup after animation
    setTimeout(function() {
      $confetti.empty();
    }, 3000);
  };

  // ---- Add to Cart ----

  QuizCoffeeMatch.prototype.addToCart = function($btn) {
    if (!this.selectedVariantId || $btn.prop('disabled')) return;

    var self = this;
    var $feedback = this.$section.find('.qcm-match__cart-feedback');
    var originalLabel = $btn.text();

    $btn.prop('disabled', true).text('Adding...');
    $feedback.text('');

    $.ajax({
      url: '/cart/add.js',
      dataType: 'json',
      type: 'POST',
      data: {
        id: self.selectedVariantId,
        quantity: 1
      },
      success: function() {
        $btn.text('Added!');
        $feedback.text('');

        setTimeout(function() {
          $btn.prop('disabled', false).text(originalLabel);
        }, 1500);

        // Refresh cart state
        $.ajax({
          url: '/cart.js',
          dataType: 'json',
          cache: false,
          success: function(cart) {
            // Update cart count in header
            if (typeof window.cart !== 'undefined' && typeof window.cart.refreshCart === 'function') {
              window.cart.refreshCart(cart);
            } else {
              // Fallback: update common cart count selectors
              var count = cart.item_count;
              $('.cart-count, .cart_count, [data-cart-count]').text(count);
            }
          }
        });
      },
      error: function(xhr) {
        var msg = 'Something went wrong. Please try again.';
        try {
          var resp = JSON.parse(xhr.responseText);
          if (resp.description) msg = resp.description;
        } catch (e) {}
        $feedback.text(msg);
        $btn.prop('disabled', false).text(originalLabel);
      }
    });
  };

  // ---- Retake ----

  QuizCoffeeMatch.prototype.retake = function() {
    this.answers = [];
    this.adventureLevel = null;
    this.preferredSize = null;
    this.selectedVariantId = null;

    // Reset all answer selections
    this.$section.find('.qcm-answer').attr('aria-checked', 'false');
    this.$section.find('[data-action="next"]').prop('disabled', true);

    // Reset gauge indicators
    this.$section.find('.qcm-gauge__indicator').css('opacity', '0');
    this.$section.find('.qcm-gauge__desc-reveal').text('');

    // Reset slider state
    this.$section.find('.qcm-slider__thumb').css('left', '-999px').attr({
      'aria-valuenow': '',
      'aria-valuetext': ''
    });
    this.$section.find('.qcm-slider__fill').css('width', '0');
    this.$section.find('.qcm-slider__stop').removeClass('is-active');
    this.$section.find('.qcm-slider__label').removeClass('is-active');
    this.$section.find('.qcm-slider__desc-reveal').text('');

    // Go back to start
    this.goToScreen(0);
  };

  // ---- Utility ----

  QuizCoffeeMatch.prototype.escapeHtml = function(str) {
    if (!str) return '';
    var div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  };

  QuizCoffeeMatch.prototype.unload = function() {
    this.$section.off('click keydown');
    this.pauseWelcomeVideos();

    if (this.welcomeVideoObserver) {
      this.welcomeVideoObserver.disconnect();
      this.welcomeVideoObserver = null;
    }

    if (this.welcomeVideoResizeHandler) {
      window.removeEventListener('resize', this.welcomeVideoResizeHandler);
      this.welcomeVideoResizeHandler = null;
    }

    clearTimeout(this.welcomeVideoResizeTimer);
  };

  // ---- Initialization ----

  function initQuiz() {
    // Guard: jQuery must be loaded (this script may execute before jQuery
    // since the section's <script defer> tag appears in the DOM before
    // theme.liquid's jQuery <script defer> tag, and defer scripts run
    // in document order).
    if (typeof jQuery === 'undefined') {
      setTimeout(initQuiz, 50);
      return;
    }
    var $ = jQuery;

    $(SECTION_SELECTOR).each(function() {
      if (!$(this).data('quiz-instance')) {
        var instance = new QuizCoffeeMatch(this);
        $(this).data('quiz-instance', instance);
      }
    });
  }

  function unloadQuiz($section) {
    var instance = $section.data('quiz-instance');
    if (instance) {
      instance.unload();
      $section.removeData('quiz-instance');
    }
  }

  // DOMContentLoaded or immediate
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initQuiz);
  } else {
    initQuiz();
  }

  // Theme editor lifecycle
  document.addEventListener('shopify:section:load', function(e) {
    if (e.target.querySelector(SECTION_SELECTOR)) {
      setTimeout(initQuiz, 100);
    }
  });

  document.addEventListener('shopify:section:unload', function(e) {
    if (typeof jQuery === 'undefined') return;
    var $section = jQuery(e.target).find(SECTION_SELECTOR);
    if ($section.length) {
      unloadQuiz($section);
    }
  });

  // Block selection in theme editor
  document.addEventListener('shopify:block:select', function(e) {
    if (typeof jQuery === 'undefined') return;
    var $ = jQuery;

    var $block = $(e.target);
    var $section = $block.closest(SECTION_SELECTOR);
    if (!$section.length) return;

    var instance = $section.data('quiz-instance');
    if (!instance) return;

    var screenIndex = parseInt($block.closest('.qcm-screen').data('screen'), 10);
    if (!isNaN(screenIndex)) {
      instance.showScreen(screenIndex);
    }
  });

  // InstantClick compatibility (register after jQuery is available)
  function registerInstantClick() {
    if (typeof InstantClick !== 'undefined') {
      InstantClick.on('change', initQuiz);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', registerInstantClick);
  } else {
    registerInstantClick();
  }
})();
